import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
// Reuse the matured-deal-slip look (msl-* classes) for a consistent ticket layout.
import './MaturedDealSlipModal.css';
import './MaturityDealTicket.css';

const DEAL_TYPES = ['Maturity', 'GSEC', 'Money Market', 'Repo', 'Reverse Repo', 'Sell/Buy', 'Buy/Sell'];

const hasCollateral = (dealType) => dealType === 'Repo' || dealType === 'Reverse Repo';

const toNum = (v) => {
  const n = Number(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) =>
  toNum(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function buildBlankRows(dealType) {
  const fm = { key: 'fund_movement', label: 'Fund Movement', value: 'No', type: 'select' };
  switch (dealType) {
    case 'GSEC':
      return [
        { key: 'seller', label: 'Seller', value: '' },
        { key: 'buyer', label: 'Buyer', value: '' },
        { key: 'face_value', label: 'Face Value', value: '' },
        { key: 'isin', label: 'Maturity', value: '' },
        { key: 'ytm', label: 'Yield to Maturity %', value: '' },
        { key: 'settlement', label: 'Settlement Value', value: '' },
        fm
      ];
    case 'Sell/Buy':
    case 'Buy/Sell':
      return [
        { key: 'seller', label: 'Seller', value: '' },
        { key: 'buyer', label: 'Buyer', value: '' },
        { key: 'face_value', label: 'Face Value', value: '' },
        { key: 'isin', label: 'Maturity', value: '' },
        { key: 'ytm', label: 'Yield to Maturity %', value: '' },
        { key: 'rate', label: 'Rate', value: '' },
        { key: 'settlement', label: 'Settlement Value', value: '' },
        { key: 'maturity_date', label: 'Maturity Date', value: '' },
        fm
      ];
    case 'Repo':
    case 'Reverse Repo':
      return [
        { key: 'value_date', label: 'Value Date', value: '' },
        { key: 'counterparty', label: 'Counterparty', value: '' },
        { key: 'maturity_date', label: 'Maturity Date', value: '' },
        { key: 'principal', label: 'Principal Amount', value: '' },
        { key: 'rate', label: 'Rate %', value: '' },
        fm
      ];
    case 'Money Market':
      return [
        { key: 'value_date', label: 'Value Date', value: '' },
        { key: 'counterparty', label: 'Counterparty', value: '' },
        { key: 'maturity_date', label: 'Maturity Date', value: '' },
        { key: 'principal', label: 'Principal Amount', value: '' },
        { key: 'rate', label: 'Interest Rate %', value: '' },
        { key: 'settlement', label: 'Settlement Value', value: '' },
        fm
      ];
    default: // 'Maturity' — generic
      return [
        { key: 'value_date', label: 'Value Date', value: '' },
        { key: 'counterparty', label: 'Counterparty', value: '' },
        { key: 'maturity_date', label: 'Maturity Date', value: '' },
        { key: 'principal', label: 'Principal / Face Value', value: '' },
        { key: 'rate', label: 'Rate / Yield %', value: '' },
        { key: 'settlement', label: 'Settlement Value', value: '' },
        fm
      ];
  }
}

const MaturityDealTicket = () => {
  const [dealType, setDealType] = useState('Maturity');
  const [rows, setRows] = useState(() => buildBlankRows('Maturity'));
  const [ticketNumber, setTicketNumber] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState('');
  const [authorizedBy, setAuthorizedBy] = useState('');
  const [collateral, setCollateral] = useState([{ isin: '', face: '' }]);

  const handleDealTypeChange = (newType) => {
    setDealType(newType);
    setRows(buildBlankRows(newType));
    if (!hasCollateral(newType)) setCollateral([{ isin: '', face: '' }]);
  };

  const updateRow = (idx, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, value } : r)));
  };

  const updateCollateral = (idx, field, value) => {
    setCollateral((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addCollateralRow = () => setCollateral((prev) => [...prev, { isin: '', face: '' }]);
  const removeCollateralRow = (idx) => setCollateral((prev) => prev.filter((_, i) => i !== idx));
  const collateralTotal = collateral.reduce((s, r) => s + toNum(r.face), 0);

  const issueTicketNumber = async () => {
    setIssuing(true);
    setIssueError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/maturity/matured-slip-number`);
      if (res.data?.success && res.data.slipNumber) {
        setTicketNumber(res.data.slipNumber);
      } else {
        setIssueError(res.data?.error || 'Failed to issue ticket number');
      }
    } catch (err) {
      setIssueError(err.response?.data?.error || err.message || 'Failed to issue ticket number');
    } finally {
      setIssuing(false);
    }
  };

  const resetTicket = () => {
    setDealType('Maturity');
    setRows(buildBlankRows('Maturity'));
    setTicketNumber('');
    setAuthorizedBy('');
    setIssueError('');
    setCollateral([{ isin: '', face: '' }]);
  };

  return (
    <div className="maturity-ticket-print-root">
      <div className="maturity-ticket-wrap">
        <div className="maturity-ticket-actions">
          <button className="msl-btn" onClick={resetTicket}>New / Clear</button>
          <button className="msl-btn" onClick={issueTicketNumber} disabled={issuing}>
            {issuing ? 'Issuing…' : 'Issue Ticket No.'}
          </button>
          <button className="msl-btn primary" onClick={() => window.print()}>Print</button>
        </div>

        {issueError && (
          <div className="mdt-no-print" style={{ color: '#c62828', padding: '8px 16px', fontSize: 13 }}>
            {issueError}
          </div>
        )}

        <div className="matured-slip-printable">
          <div className="msl-header">
            <div className="msl-deal-type">
              <span className="msl-dt-label">Deal Type</span>
              <select
                className="mdt-select msl-dt-input"
                value={dealType}
                onChange={(e) => handleDealTypeChange(e.target.value)}
              >
                {DEAL_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="msl-slip-no">
              <input
                className="msl-input msl-slip-no-input"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                placeholder="YYYYMM-####"
              />
            </div>
          </div>

          <table className="msl-fields">
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.key}>
                  <td className="msl-field-label">{row.label}</td>
                  <td className="msl-field-value">
                    {row.type === 'select' ? (
                      <select
                        className="mdt-select"
                        value={row.value}
                        onChange={(e) => updateRow(idx, e.target.value)}
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    ) : (
                      <input
                        className="msl-input"
                        value={row.value}
                        onChange={(e) => updateRow(idx, e.target.value)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {hasCollateral(dealType) && (
            <div className="msl-collateral">
              <div className="msl-collateral-title">Collateral Allocation (ISIN-wise Face Value)</div>
              <table className="msl-collateral-table">
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>#</th>
                    <th>ISIN</th>
                    <th style={{ width: '35%' }}>Face Value Allocated</th>
                    <th className="msl-no-print mdt-no-print" style={{ width: '8%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {collateral.map((r, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <input
                          className="msl-input"
                          value={r.isin}
                          onChange={(e) => updateCollateral(idx, 'isin', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="msl-input msl-num"
                          value={r.face}
                          onChange={(e) => updateCollateral(idx, 'face', e.target.value)}
                        />
                      </td>
                      <td className="msl-no-print mdt-no-print">
                        <button className="msl-row-remove" onClick={() => removeCollateralRow(idx)}>×</button>
                      </td>
                    </tr>
                  ))}
                  <tr className="msl-collateral-total">
                    <td colSpan={2}>Total</td>
                    <td className="msl-num">{fmtMoney(collateralTotal)}</td>
                    <td className="msl-no-print mdt-no-print"></td>
                  </tr>
                </tbody>
              </table>
              <button className="msl-btn msl-no-print mdt-no-print" onClick={addCollateralRow}>+ Add ISIN row</button>
            </div>
          )}

          <div className="msl-signature">
            <div className="msl-sign-line">…….........................................................</div>
            <div className="msl-sign-row">
              <span className="msl-sign-label">Authorized By</span>
              <input
                className="msl-input msl-sign-input"
                value={authorizedBy}
                onChange={(e) => setAuthorizedBy(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaturityDealTicket;
